import React from 'react';
import classNames from 'classnames';
import {
  isLayoutPGOneColumn,
  isLayoutPGSideBySide,
  isLayoutProGallery,
  type LayoutSettingsType,
} from '@wix/communities-blog-client-common';
import { useResponsiveContext } from '@app/external/common/components/responsive-listener/responsive-context';
import { useSelector } from '@app/external/common/components/runtime-context';
import useLayoutProps from '@app/external/common/hooks/use-layout-props';
import {
  getIsCreatedWithResponsiveEditor,
  getLayoutMargins,
  getLayoutPostSize,
} from '@app/external/common/selectors/app-settings-selectors';
import { isUsingResponsiveStyling } from '@app/external/common/services/post-list-pro-gallery';
import styles from './feed-layout-box.scss';

type Props = React.PropsWithChildren<{
  contentClassName?: string;
  noPadding?: boolean;
}> &
  React.HTMLAttributes<HTMLDivElement>;

const FeedLayoutBox: React.FC<Props> = ({
  children,
  className,
  contentClassName,
  noPadding,
  ...props
}) => {
  const { contentSize, padding } = useFeedContentSizeForLayout();
  const effectivePadding = noPadding ? 0 : padding;

  return (
    <div
      className={classNames(styles.root, className)}
      style={{
        '--padding': `${effectivePadding}px`,
        '--content-size': contentSize ? `${contentSize}px` : undefined,
      }}
      {...props}
    >
      <div className={classNames(styles.content, contentClassName)}>{children}</div>
    </div>
  );
};

function useFeedContentSizeForLayout() {
  const { section, layoutType } = useLayoutProps();

  const layoutPgSideBySide = isLayoutPGSideBySide(layoutType);
  const layoutPgOneColumn = isLayoutPGOneColumn(layoutType);
  const layoutMargins = useSelector((state) => getLayoutMargins({ state })) ?? 20;
  const createdWithResponsiveEditor = useSelector(getIsCreatedWithResponsiveEditor);
  const useResponsiveEditorStyling = isUsingResponsiveStyling({
    createdWithResponsiveEditor,
    section,
  });

  const { rootWidth } = useResponsiveContext();
  const layoutPostSize = useSelector((state, host) => {
    return getLayoutPostSize({
      state,
      section,
      layoutType: layoutType as LayoutSettingsType,
      hostWidth: host.dimensions.width,
      rootWidth,
      layoutMargins: useResponsiveEditorStyling ? undefined : layoutMargins,
    });
  });

  let padding: number | undefined;
  let contentSize: number | undefined;

  if (layoutPgSideBySide || layoutPgOneColumn) {
    contentSize = layoutPostSize;
  } else if (!isLayoutProGallery(layoutType!)) {
    contentSize = 940;
  }

  if (!layoutPgSideBySide && !layoutPgOneColumn && !useResponsiveEditorStyling) {
    padding = layoutMargins;
  }

  return { contentSize, padding };
}

export default FeedLayoutBox;
